<template>
  <div class="flex flex-col items-center h-full overflow-y-auto bg-gray-100 pt-2">
    <!-- Main Content -->
    <img src="../../../assets/img/admin/referral-img.png" class="w-[35%]" />
<!--    <ConfettiExplosion />-->
    <h2 class="text-lg font-medium text-gray-800 px-12 text-center pt-2">שתפו אותנו עם חברים, ותוכלו לקבל BuyMe
      עלינו! </h2>
    <div class="px-2 py-4">
      <!-- Share Section -->
      <TCard class="mb-4 relative overflow-hidden">
        <h3 class="font-semibold text-gray-800 mb-4 flex items-center gap-2">
          איך זה עובד?
        </h3>
        <div class="space-y-3">
          <div
            v-for="(step, index) in howItWorks"
            :key="index"
            class="flex gap-3 items-center"
          >
            <div class="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
              <span class="text-purple-600 font-semibold text-sm">{{ index + 1 }}</span>
            </div>
            <p class="text-sm text-gray-700">{{ step }}</p>
          </div>
        </div>

        <div class="flex items-center justify-between my-4 border p-2 rounded-lg">
          <div class="flex items-center gap-3">
            <img src="../../../assets/img/admin/buyme-logo.png" class="w-12 h-12 rounded-xl" alt="">
            <div>
              <h2 class="text-lg font-bold text-gray-800">מזמינים, מצרפים ומצ׳ופרים </h2>
              <p class="text-xs text-gray-600">כדי להראות את הערכתנו נשלח לכם ביימי על סך 75₪</p>
            </div>
          </div>
        </div>

        <Button
          label="העתיקו את הקישור"
          icon="pi pi-copy"
          class="w-full"
          @click="copyReferralLink"
        />
      </TCard>

      <!-- Referrals List -->
      <TCard class="w-full flex flex-col">
        <div class="flex items-center gap-2 mb-4">
          <i class="pi pi-users text-purple-600"></i>
          <h3 class="font-semibold text-gray-800">ההזמנות ששלחתם</h3>
        </div>

        <div class="space-y-2" v-if="referrals.length > 0">
          <Motion
            v-for="(referral, index) in referrals"
            :key="referral.id"
            :initial="{ opacity: 0, x: -20 }"
            :animate="{ opacity: 1, x: 0 }"
            :transition="{ delay: index * 0.05 }"
          >
            <div
              class="flex items-center justify-between pb-3 rounded-xl hover:bg-gray-50 transition-colors duration-200"
              :class="{ 'bg-green-50': referral.status === ReferralStatus.REWARD_SENT }"
            >
              <div class="flex items-center gap-3">
                <Avatar
                  :label="referral.referredBusinessName.charAt(0)"
                  shape="circle"
                  class="text-white !bg-primary-300"
                />
                <div>
                  <p class="font-medium text-gray-800">{{ referral.referredBusinessName }}</p>
                  <p class="text-sm text-gray-500">{{ formatDate(referral.signupDate as string) }}</p>
                </div>
              </div>

              <div class="flex items-center gap-2">
                <Chip
                  :label="getStatusLabel(referral.status)"
                  :class="getStatusClass(referral.status)"
                />
                <Motion
                  v-if="referral.status === ReferralStatus.REWARD_SENT"
                  :initial="{ scale: 0 }"
                  :animate="{ scale: 1 }"
                >
                  <i class="pi pi-check-circle text-green-600 text-xl"></i>
                </Motion>
              </div>
            </div>
          </Motion>
        </div>
        <span class="text-sm w-full text-center" v-else>עוד לא הזמנתם חברים, למה אתם מחכים? 😉</span>
      </TCard>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useToast } from 'vue-toastification';
// import ConfettiExplosion from 'vue-confetti-explosion';
import { Motion } from '@motionone/vue';
import { DateTime } from 'luxon';
import TCard from '@app/components/TCard.vue';
import { useReferrals } from '@app/composables/referrals';
import { ReferralApi } from '@app/api/admin/referral-api';
import { IReferralItem, ReferralStatus } from '@torim-app/types';
import { trackEvent } from '@app/composables/posthog';

const toast = useToast();
const { copyReferralLink: _copyReferralLink } = useReferrals();

const copyReferralLink = () => {
  trackEvent('referral_link_copied');
  _copyReferralLink();
};

const referrals = ref<IReferralItem[]>([]);

// Computed properties


const howItWorks = [
  'שתפו את הקישור האישי שלכם עם חברים שטיימאיט יכולה לשפר להם את העסק',
  'החברים שלכם מצטרפים דרך הקישור שלכם',
  'החברים שלכם מנסים אותנו, מתאהבים ולוקחים את חבילת #הכל_כלול שלנו ',
  'לאחר 60 יום של מנוי פעיל, אתם מקבלים שובר מתנה של 75 ₪!'
];


const getStatusLabel = (status: string) => {
  const labels: Record<string, string> = {
    signed_up: 'נרשם',
    premium: 'פרימיום',
    rewarded: 'מתנה נשלחה'
  };
  return labels[status] || status;
};

const getStatusClass = (status: string) => {
  const classes: Record<string, string> = {
    signed_up: '!bg-gray-100 !text-gray-700',
    premium: '!bg-gray-100 !text-gray-700',
    rewarded: '!bg-transparent !text-green-700'
  };
  return classes[status] || '';
};

const formatDate = (dateString: string) => {
  return DateTime.fromISO(dateString).toLocaleString(DateTime.DATE_MED);
};

onMounted(() => {
  trackEvent('referral_dashboard_viewed');
  ReferralApi.fetchAllReferrals()
    .then(res => {
      referrals.value.push(...res)
    }).catch(err => {
    console.log(err);
    toast.error('לא הצלחנו למצוא את רשימת המוזמנים שלכם, אנא נסו שוב.');
  } )
})

</script>
